// bezakli-preview — w23 Bezakli Atlas.
//
// Paper-invitation preview. A warm-gold foil border wraps a cream inner
// card. The focal medallion is a suzani-inspired 8-point stamp: an
// eight-leaf rosette around a gold star, the way Bukhara-traditional
// designs centre the page. Eyebrow reads TO'Y MAROSIMI (wedding). The
// font is Cormorant Garamond and colours flow from LayoutCtx so the
// catalog and printed card stay in sync.
import { type LayoutCtx } from "./_shared";

const FOIL =
  "linear-gradient(135deg, #876130 0%, #B3893F 22%, #DDC07A 45%, #B3893F 65%, #876130 100%)";

export function BezakliPreviewLayout({ names, date, venue, accent, ink, bg, font }: LayoutCtx) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ width: 14, height: 1, background: accent }} />
          TO'Y MAROSIMI
          <span style={{ width: 14, height: 1, background: accent }} />
        </div>

        {/* Suzani 8-point rosette medallion */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FOIL,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "6%",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg viewBox="0 0 100 100" style={{ width: "88%", height: "88%" }} aria-hidden>
                {/* 8 suzani leaves radiating */}
                {Array.from({ length: 8 }).map((_, i) => {
                  const deg = i * 45;
                  return (
                    <g key={`l${i}`} transform={`rotate(${deg} 50 50)`}>
                      <path
                        d="M50 50 Q 56 30 50 14 Q 44 30 50 50 Z"
                        fill={accent}
                        opacity="0.85"
                      />
                      <path
                        d="M50 50 Q 56 30 50 14"
                        stroke="#FFFFFF"
                        strokeWidth="0.4"
                        fill="none"
                        opacity="0.45"
                      />
                    </g>
                  );
                })}
                {/* Inner ring */}
                <circle cx="50" cy="50" r="14" fill={bg} stroke={accent} strokeWidth="0.9" />
                {/* 8-point star at centre */}
                {Array.from({ length: 8 }).map((_, i) => {
                  const deg = i * 45;
                  return (
                    <g key={`s${i}`} transform={`rotate(${deg} 50 50)`}>
                      <path
                        d="M50 50 L52 42 L50 36 L48 42 Z"
                        fill={accent}
                        opacity="0.9"
                      />
                    </g>
                  );
                })}
                <circle cx="50" cy="50" r="3" fill={accent} />
                {/* Outer ring */}
                <circle cx="50" cy="50" r="46" fill="none" stroke={accent} strokeWidth="0.6" opacity="0.55" />
              </svg>
            </div>
          </div>
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 500,
            color: ink,
            letterSpacing: "0.01em",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Footer */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TO'Y MAROSIMI · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
